import React, { useState, useEffect, useRef, useContext } from 'react';
import { useChat } from './ChatContext';
import { pushBackHandler } from '../../native/backHandler';
import { AuthContext } from '../Provider';
import { FaRegCommentDots } from "react-icons/fa";
import ConversationsList from './ConversationsList';
import ChatWindow from './ChatWindow';
import NotificationDisplay from './NotificationDisplay';
import { supabase } from '../data/supabaseClient';
import { useTranslation } from 'react-i18next'; // Import translation hook
import Spinner from '../common/Spinner'; // Import the new Spinner component
import './ChatV2.css';

const ChatV2 = () => {
  // Get translation function
  const { t } = useTranslation('common');
  
  // Get user from AuthContext
  const { user, loading: authLoading } = useContext(AuthContext);
  
  // Get necessary values from ChatContext
  const {
    isChatOpen,
    toggleChat,
    closeChat,
    activeConversationId,
    openChatWithConversation,
    markConversationAsRead,
    addOtherUnreadMessage,
    addNotification,
    otherUnreadMessages,
    clearOtherUnreadMessage,
    showConversations,
    occurrenceContext // Get occurrence context for recurring posts
  } = useChat();

  // Local state
  const [currentUser, setCurrentUser] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const messageSubscriptionRef = useRef(null);

  // Refs so the global-message subscription effect can run ONCE per user instead of
  // tearing down + re-subscribing (and flashing the loading spinner) on every
  // open/close/conversation-switch.
  const isChatOpenRef = useRef(isChatOpen);
  const activeConversationIdRef = useRef(activeConversationId);
  const addOtherUnreadMessageRef = useRef(addOtherUnreadMessage);
  const addNotificationRef = useRef(addNotification);
  useEffect(() => {
    isChatOpenRef.current = isChatOpen;
  }, [isChatOpen]);
  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);
  useEffect(() => {
    addOtherUnreadMessageRef.current = addOtherUnreadMessage;
  }, [addOtherUnreadMessage]);
  useEffect(() => {
    addNotificationRef.current = addNotification;
  }, [addNotification]);

  // Get current user and set up message subscription
  useEffect(() => {
    // Guard: Don't run if user from AuthContext is not available
    if (!user) {
      setCurrentUser(null);
      setIsLoading(false);
      return;
    }

    const fetchUser = async () => {
      try {
        setIsLoading(true);
        const { data: { user: authUser }, error: userError } = await supabase.auth.getUser();

        if (userError) {
          throw userError;
        }

        // Guard: If no session, don't proceed
        if (!authUser) {
          setCurrentUser(null);
          setIsLoading(false);
          return;
        }

        setCurrentUser(authUser);

        // Set up message subscription with better unread tracking
        const channel = supabase
          .channel('global-messages')
          .on('postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'messages'
            },
            async (payload) => {
              // Only proceed if message is not from current user
              if (payload.new.user_id === authUser.id) return;

              try {
                // Participant check + sender profile in parallel. This single
                // subscription now also drives the in-chat notification popup
                // (previously a duplicate table-wide subscription in RideNotifications).
                const [participantRes, senderRes] = await Promise.all([
                  supabase
                    .from('conversation_participants')
                    .select('conversation_id')
                    .eq('conversation_id', payload.new.conversation_id)
                    .eq('user_id', authUser.id)
                    .single(),
                  supabase
                    .from('profiles')
                    .select('username, avatar_url')
                    .eq('id', payload.new.user_id)
                    .single()
                ]);

                const { data: participant, error: participantError } = participantRes;
                if (participantError && participantError.code !== 'PGRST116') {
                  throw participantError;
                }
                if (!participant) return;

                // Mark message as delivered immediately (user is online and received it)
                await supabase
                  .from('message_read_status')
                  .update({
                    is_delivered: true,
                    delivered_at: new Date().toISOString(),
                    updated_at: new Date().toISOString()
                  })
                  .eq('message_id', payload.new.id)
                  .eq('user_id', authUser.id)
                  .eq('is_delivered', false);

                // Only add to unread if chat is closed OR we're in a different conversation
                if (!isChatOpenRef.current || payload.new.conversation_id !== activeConversationIdRef.current) {
                  addOtherUnreadMessageRef.current(payload.new.conversation_id);
                }

                // In-chat notification popup. addNotification self-guards when the
                // matching conversation is already open, so no extra check needed.
                if (addNotificationRef.current) {
                  const senderProfile = senderRes?.data;
                  addNotificationRef.current({
                    type: 'new_message',
                    conversationId: payload.new.conversation_id,
                    sender: senderProfile?.username || t('chat.unknown_user'),
                    message: payload.new.text,
                    timestamp: new Date().toISOString(),
                    avatarUrl: senderProfile?.avatar_url
                  });
                }
              } catch (error) {
                console.error('Error processing incoming message:', error);
              }
            }
          )
          .subscribe();

        messageSubscriptionRef.current = channel;
      } catch (error) {
        console.error('Error fetching user:', error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();

    // Cleanup subscription
    return () => {
      if (messageSubscriptionRef.current) {
        supabase.removeChannel(messageSubscriptionRef.current);
        messageSubscriptionRef.current = null;
      }
    };
    // Run once per user — the subscription reads live values via refs (above), so it
    // does NOT re-subscribe on open/close/conversation-switch.
  }, [user]);

  // Handle conversation selection
  const handleConversationSelect = (conversationId) => {
    setSelectedConversation(conversationId);
    markConversationAsRead(conversationId);
    openChatWithConversation(conversationId);
    clearOtherUnreadMessage(conversationId);
  };
  
  // Clear unread messages when opening chat or switching conversations
  useEffect(() => {
    if (isChatOpen && activeConversationId) {
      clearOtherUnreadMessage(activeConversationId);
    }
  }, [isChatOpen, activeConversationId, clearOtherUnreadMessage]);

  // Handle back button click — clear BOTH the local selection and the context's
  // activeConversationId (set when opened from a post/notification), otherwise the
  // active conversation keeps rendering instead of returning to the list.
  const handleBackToList = () => {
    setSelectedConversation(null);
    showConversations();
  };

  // When the panel is closed, drop the local selection so reopening via the
  // toggle button starts at the conversation list rather than the last chat.
  useEffect(() => {
    if (!isChatOpen) {
      setSelectedConversation(null);
    }
  }, [isChatOpen]);

  // Effect to mark conversations as read when selected
  useEffect(() => {
    if (selectedConversation) {
      markConversationAsRead(selectedConversation);
      clearOtherUnreadMessage(selectedConversation);
    }
  }, [selectedConversation, markConversationAsRead, clearOtherUnreadMessage]);

  // Don't clear unread messages when just opening the chat
  // Messages should only be marked as read when actually viewing the conversation
  // This was removed to fix the issue where indicators disappeared without viewing messages

  // Lock page scroll while the chat is open (CSS scopes this to mobile only).
  // The scroll root is <html>, so lock the documentElement.
  useEffect(() => {
    const root = document.documentElement;
    if (isChatOpen) {
      root.classList.add('chat-open-lock');
      document.body.classList.add('chat-open-lock');
    } else {
      root.classList.remove('chat-open-lock');
      document.body.classList.remove('chat-open-lock');
    }
    return () => {
      root.classList.remove('chat-open-lock');
      document.body.classList.remove('chat-open-lock');
    };
  }, [isChatOpen]);

  // Android back button closes the chat instead of navigating the page / exiting.
  useEffect(() => {
    if (!isChatOpen) return undefined;
    return pushBackHandler(() => closeChat());
  }, [isChatOpen, closeChat]);

  // Determine what to render in the chat container
  const renderChatContent = () => {
    // If there's an error, show error message
    if (error) {
      return (
        <div className="chat-error">
          <p>{t('chat.error')}: {error}</p>
          <button 
            className="retry-button" 
            onClick={() => window.location.reload()}
          >
            {t('buttons.retry')}
          </button>
        </div>
      );
    }

    // If still loading, show spinner
    if (isLoading) {
      return (
        <div className="chat-loading">
          <Spinner size="medium" customText={t('chat.loading')} />
        </div>
      );
    }

    // If there's an active conversation (coming from post), show it directly
    if (activeConversationId) {
      return (
        <ChatWindow
          conversationId={activeConversationId}
          onBack={handleBackToList}
          occurrenceContext={occurrenceContext}
        />
      );
    }

    // If there's a selected conversation from the list, show it
    if (selectedConversation) {
      return (
        <ChatWindow
          conversationId={selectedConversation}
          onBack={handleBackToList}
          occurrenceContext={occurrenceContext}
        />
      );
    }

    // Otherwise show the conversations list
    return <ConversationsList onSelect={handleConversationSelect} />;
  };

  // Don't render anything if user is not authenticated
  if (!user || authLoading) {
    return null;
  }

  return (
    <>
      {/* Dim backdrop behind the chat (mobile only, via CSS) */}
      {isChatOpen && (
        <div
          className="chat-backdrop"
          onClick={toggleChat}
          aria-hidden="true"
        />
      )}

      {/* Chat container */}
      {isChatOpen && (
        <div className="chat-container">
          {renderChatContent()}
        </div>
      )}

      {/* Chat toggle button */}
      <div className="chat-toggle-container">
        <button 
          className="chat-toggle-button" 
          onClick={toggleChat}
          aria-label={t('chat.toggle_chat')}
        >
          <FaRegCommentDots size={24} />
          {otherUnreadMessages.size > 0 && (
            <div className="unread-count">{otherUnreadMessages.size}</div>
          )}
        </button>
      </div>
    </>
  );
};

export default ChatV2;